import {
  channelInfo,
  dealwithChannelChartKey,
} from '@op/components/ChannelLabel';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import { NO_DATE } from '@op/constants';
import { RequestFuncMapType } from '@op/pages/Overview/KeyMetrics/hooks/channel';
import { expandableLeftChartHeight } from '@op/pages/Summary/constants';
import { getPercentageInfo } from '@op/utils';
import {
  formatTimeXAxisText,
  getChartPosition,
  getXAxisAxisLabel,
} from '@op/utils/chart';
import { dealWithOthersName } from '@op/utils/languageValue';
import {
  decimalFormatPercentage,
  eChartPercentageYAxis,
  reviewsFormat,
  yAxisFormat,
} from '@op/utils/value';
import React from 'react';
import { useMemo } from 'react';
import { useIntl } from 'umi';

export default function LineChart({
  timeline,
  filter,
  chartType = 'line',
  isRatio = false,
  type,
  translate,
}: {
  timeline: any;
  filter: any;
  chartType?: string;
  isRatio?: boolean;
  type: RequestFuncMapType;
  translate?: (text: string) => string;
}) {
  const { formatMessage } = useIntl();

  function translateInfo(name: any): { img?: string; text: string } {
    // const newName: any = dealWithOthersName(name, 'language');

    if (['channel', 'channel_news'].includes(type)) {
      const { img, name: text } = channelInfo(name);
      return { img, text };
    }

    if (['language', 'language_news', 'country'].includes(type)) {
      return { text: translate?.(name) || NO_DATE.EMPTY };
    }

    return { text: formatMessage({ id: name }) };
  }

  const createLineChartOptions = () => {
    const formatValue = isRatio ? decimalFormatPercentage : reviewsFormat;
    let { newArr, timeArr: xAxisData, legendData } = timeline || {};
    let isTooMin = false;
    if (isRatio) {
      const value = (newArr || []).map((item) => item.data).flat(2);
      const maxValue = Math.max(...value);
      isTooMin = maxValue <= 0.1;
    }
    return {
      grid: {
        top: 20,
        contentLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        appendToBody: true,
        confine: false,
        position: (pos: any, params: any, dom: any, rect: any, size: any) =>
          getChartPosition(pos, params, dom, rect, size, -5),
        formatter: (params: any) => {
          let currentDateTotal = 0;
          let { name } = params?.[0] || {};
          params?.forEach((item: any) => (currentDateTotal += item?.value));
          const time = listTimeTitleFunc(name, filter.date_type);
          return formatChartParamsDate({
            params,
            total: currentDateTotal,
            isSort: true,
            valueFormat: (value: number) => formatValue(value),
            listTitleFunc: (item: any) => {
              const { text, img } = translateInfo(item.seriesName);
              if (img) {
                return `<span class="dbo-chart-formatter-img"><img src=${img} class="chart-common-formatter-img" /> ${text}<span>`;
              }
              return text;
            },
            dealwithRecords(records, total) {
              if (isRatio) return records;

              const newRecords: Array<any> = records.map((item) => {
                const share = getPercentageInfo(item.originValue, total, 2);
                return {
                  ...item,
                  value: `${item.value} ${
                    item.originValue ? `(${share})` : ''
                  }`,
                };
              });
              newRecords.unshift({
                text: formatMessage({ id: 'Sum' }),
                value: reviewsFormat(total),
                originValue: total,
                originText: '',
              });

              return newRecords.filter((item) => Boolean(item));
            },
            tablesConfigFunc: (records: Array<any>) => [
              {
                showHeader: true,
                records,
                columns: [
                  {
                    key: 'text',
                    header: formatMessage({ id: 'Mentions' }),
                  },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: time,
                  },
                ],
              },
            ],
          });
        },
      },
      legend: {
        data: legendData,
        formatter: (params: string) => {
          const { text, img } = translateInfo(params);
          if (img) {
            const icon = dealwithChannelChartKey(params);
            return `{${icon}|} {name|${text}}`;
          }
          return text;
        },
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filter.date_type),
      ),
      xAxis: {
        type: 'category',
        axisLabel: getXAxisAxisLabel(filter),
        data: xAxisData,
        boundaryGap: ['20%', 0],
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: any) =>
            isRatio
              ? eChartPercentageYAxis(value, isTooMin ? 2 : 0)
              : yAxisFormat(value),
        },
      },
      series: (newArr || []).map((val: any, index: number) => ({
        ...val,
        stack: chartType === 'bar' ? 'Total' : `Total${index}`,
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 8,
        type: chartType,
        emphasis: {
          showSymbol: true,
        },
      })),
    };
  };

  const options = useMemo(() => {
    return createLineChartOptions();
  }, [formatMessage, timeline, chartType]);

  const empty = useMemo(() => {
    let { newArr } = timeline;

    return (newArr || []).every((item: any) => !item.data);
  }, [timeline]);

  return (
    <EChartCommon
      empty={empty}
      hasEmptyImg={false}
      hasEmptyTitle={false}
      options={options}
      optionsProps={{ type: chartType }}
      height={expandableLeftChartHeight + 10}
    />
  );
}
